import Text from '@/components/cms/Text';
import { replacePlaceholder } from '@/components/ui/CreditPlan/util/CreditPlanHelpers';
import CreditDonut from '@/components/ui/CreditTracker/snippets/CreditDonut';
import Shimmer from '@/components/ui/Shimmer';
import Button from '@/components/ui/Buttons/Button';
import classnames from 'classnames';
import { FC, ReactNode, useCallback, useEffect, useMemo } from 'react';
import {
  PromoEvent,
  trackSelectPromotionEvent,
  trackViewPromotionEvent
} from '@/analytics/ecommerce';
import { defaultDonutColors } from '@/components/ui/CreditTracker/util/constants';
import Requirements from './Requirements';
import { CreditPlanMiniTrackerBaseProps } from './constants';

interface CreditPlanMiniTrackerNonPersonalizedProps extends CreditPlanMiniTrackerBaseProps {
  jurisdictionName?: string;
  isLoading?: boolean;
  isChildPlan?: boolean;
  donutCounterText?: string;
  header?: string;
  bodyText?: string;
  donutTooltip?: ReactNode;
  promoEvent?: PromoEvent;
}

const CreditPlanMiniTrackerNonPersonalized: FC<CreditPlanMiniTrackerNonPersonalizedProps> = ({
  creditsCounted,
  creditsEarned,
  creditsRequired,
  creditBreakdownCreditTypes,
  jurisdictionName,
  jurisdictionMessage,
  allOtherCreditsTooltip,
  showDonut,
  isLoading,
  isChildPlan,
  donutCounterText,
  header,
  bodyText,
  primaryLink,
  donutTooltip,
  itemAnalyticsModel,
  itemListAnalyticsModel,
  promoEvent
}) => {
  const shimmer = useMemo(
    () => (
      <div className="flex w-full flex-col rounded-xl border border-silver lg:basis-1/2 lg:flex-row lg:flex-nowrap lg:justify-between">
        <div className="flex basis-1/2 flex-row flex-nowrap items-center justify-between rounded-t-xl bg-gray-lightest px-6 py-8 lg:rounded-l-xl lg:rounded-tr-none lg:p-[52px]">
          <div className="flex w-full flex-col items-start justify-between md:flex-row-reverse lg:flex-row">
            {!!showDonut && (
              <CreditDonut
                creditsCounted={0}
                creditsRequired={0}
                creditsEarned={0}
                donutColors={defaultDonutColors}
                isLoading={true}
                donutSize="creditPlan"
                className="mb-7 md:mb-0 lg:mr-10"
              />
            )}
            <div className="flex w-full flex-col items-start">
              <Shimmer className="h-24 w-3/4" />
              <Shimmer className="mt-2 h-10 w-full" />
              <Shimmer className="mt-2 h-14 w-1/2" />
            </div>
          </div>
        </div>
        <div className="flex w-full flex-row items-center justify-start rounded-b-xl px-8 py-6 lg:basis-1/2 lg:rounded-r-xl lg:rounded-bl-none lg:px-10">
          <div className="w-full">
            {Array(4)
              .fill(0)
              .map((_, i) => (
                <div className="py-6" key={i}>
                  <div className="flex w-full flex-row items-center justify-between">
                    <Shimmer className="h-10 w-3/4" />
                    <Shimmer className="h-8 w-1/5" />
                  </div>
                </div>
              ))}
          </div>
        </div>
      </div>
    ),
    [showDonut]
  );

  const primaryButtonOnClick = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      if (promoEvent)
        trackSelectPromotionEvent(promoEvent, itemAnalyticsModel, itemListAnalyticsModel);
      primaryLink?.onClick?.(e);
    },
    [itemAnalyticsModel, itemListAnalyticsModel, primaryLink, promoEvent]
  );

  useEffect(() => {
    if (primaryLink && !isLoading && !!promoEvent)
      trackViewPromotionEvent(promoEvent, itemAnalyticsModel, itemListAnalyticsModel);
  }, [isLoading, itemAnalyticsModel, itemListAnalyticsModel, primaryLink, promoEvent]);

  return isLoading ? (
    shimmer
  ) : (
    <div className="flex h-auto min-h-[266px] w-full flex-col rounded-lg border border-silver lg:basis-1/2 lg:flex-row lg:flex-nowrap lg:justify-between">
      <div className="flex basis-1/2 flex-row flex-nowrap items-center justify-between rounded-t-lg bg-gray-lightest px-6 py-8 lg:rounded-l-lg lg:rounded-tr-none lg:p-[52px]">
        <div
          className={classnames('flex w-full flex-col items-start justify-between lg:flex-row', {
            'md:flex-row-reverse': !!showDonut
          })}
        >
          {!!showDonut && (
            <CreditDonut
              creditsCounted={creditsCounted}
              creditsRequired={creditsRequired}
              creditsEarned={creditsEarned}
              donutColors={defaultDonutColors}
              counterTextOverride={donutCounterText ?? (isChildPlan ? jurisdictionName : undefined)}
              counterTextOverrideClassName={donutCounterText ? 'heading-4' : undefined}
              donutSize="creditPlan"
              className="mb-7 md:mb-0 lg:mr-10"
              forceShowTooltip={!!donutTooltip}
              tooltip={donutTooltip}
            />
          )}
          <div className="flex flex-col items-start">
            {!!header && (
              <Text
                propertyName="Header"
                element="h5"
                className="heading-4 bg-gradient bg-clip-text text-transparent"
              >
                {jurisdictionName
                  ? replacePlaceholder(header, 'Jurisdiction', jurisdictionName)
                  : header}
              </Text>
            )}
            {bodyText ? (
              <Text propertyName="Body" element="p" className="text-1 mt-2">
                {bodyText}
              </Text>
            ) : creditsEarned > 0 ? (
              <Text element="h5" className="heading-4 mt-2">
                {`${creditsEarned} credits in plan`}
              </Text>
            ) : undefined}
            {!!primaryLink && (
              <Button
                {...primaryLink}
                onClick={primaryButtonOnClick}
                className="mt-6 lg:h-14"
                size="short"
                label={primaryLink.text || ''}
              />
            )}
          </div>
        </div>
      </div>
      <Requirements
        creditBreakdownCreditTypes={creditBreakdownCreditTypes}
        allOtherCreditsTooltip={allOtherCreditsTooltip}
        jurisdictionMessage={jurisdictionMessage}
        className="rounded-b-lg px-6 py-8 lg:basis-1/2 lg:rounded-r-lg lg:rounded-bl-none lg:px-10 lg:py-8"
      />
    </div>
  );
};
export default CreditPlanMiniTrackerNonPersonalized;
